/**
 * All data tables for the OP Sync dashboard.
 *
 * Uses the design-system Table, Badge, Skeleton, and Tooltip components
 * for consistent styling, proper loading skeletons, and tooltip-on-truncate.
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateTime } from './shared';
import type {
  OPSyncAuditResult,
  OPSyncAuditFieldSummary,
  OPSyncFieldComparison,
  OPSyncAuditRun,
  OPSyncAuditRunItem,
  OPSyncJobStatus,
} from '@/lib/api';

/* ---------- Shared helpers ---------- */

function TableSkeleton({ rows = 3, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {Array.from({ length: cols }, (_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-16" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: rows }, (_, r) => (
          <TableRow key={r}>
            {Array.from({ length: cols }, (_, c) => (
              <TableCell key={c}>
                <Skeleton className="h-3 w-14" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-20 text-center text-muted-foreground">
        {message}
      </TableCell>
    </TableRow>
  );
}

function TruncatedCell({
  value,
  className,
}: {
  value: string | null | undefined;
  className?: string;
}) {
  if (!value) return <span className="text-muted-foreground">{'—'}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`block max-w-[280px] truncate cursor-help ${className ?? ''}`}>
          {value}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-sm">
        <p className="text-xs break-all">{value}</p>
      </TooltipContent>
    </Tooltip>
  );
}

const severityVariant = (s: string) =>
  s === 'CRITICAL' ? 'destructive' : s === 'WARNING' ? 'warning' : 'info';

const statusVariant = (s: string) =>
  s === 'Completed' ? 'success'
    : s === 'Detected' ? 'warning'
    : s === 'Failed' ? 'destructive'
    : s === 'InProgress' ? 'info'
    : 'secondary';

const detectionActionVariant = (action: OPSyncAuditRunItem['DetectionAction']) =>
  action === 'INSERT' ? 'info'
    : action === 'UPDATE' ? 'warning'
    : 'secondary';

const comparisonResultVariant = (comparison: OPSyncFieldComparison) =>
  comparison.match ? 'success' : severityVariant(comparison.severity);

function formatCompareValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Format a JSON value compactly: simple objects ({type, value}) stay on one
 * line, arrays get one entry per line. Much denser than JSON.stringify(…, 2).
 */
function compactJson(val: unknown, depth = 0): string {
  if (val === null || val === undefined) return 'null';
  if (typeof val === 'string') return `"${val}"`;
  if (typeof val !== 'object') return String(val);

  // Simple object (≤3 keys, all scalar values): inline
  if (!Array.isArray(val)) {
    const entries = Object.entries(val as Record<string, unknown>);
    const allScalar = entries.every(
      ([, v]) => typeof v !== 'object' || v === null,
    );
    if (allScalar && entries.length <= 3) {
      const inner = entries
        .map(([k, v]) => `"${k}": ${compactJson(v)}`)
        .join(', ');
      return `{ ${inner} }`;
    }
    // Complex object: one key per line
    const pad = '  '.repeat(depth + 1);
    const closePad = '  '.repeat(depth);
    const lines = entries.map(
      ([k, v]) => `${pad}"${k}": ${compactJson(v, depth + 1)}`,
    );
    return `{\n${lines.join(',\n')}\n${closePad}}`;
  }

  // Array of simple objects: one per line, each object inline
  const arr = val as unknown[];
  if (arr.length === 0) return '[]';
  const items = arr.map((item) => compactJson(item, depth + 1));
  const pad = '  '.repeat(depth + 1);
  const closePad = '  '.repeat(depth);
  return `[\n${items.map((s) => `${pad}${s}`).join(',\n')}\n${closePad}]`;
}

/**
 * Renders a comparison value with full visibility.
 * JSON objects/arrays use a compact pretty format.
 * Plain strings wrap normally. No truncation.
 */
function ComparisonValueCell({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground">—</span>;
  }

  // Objects/arrays: compact pretty-print
  if (typeof value === 'object') {
    return (
      <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-snug">
        {compactJson(value)}
      </pre>
    );
  }

  const str = String(value);

  // Strings that look like JSON: parse and compact-print
  const trimmed = str.trim();
  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      const parsed = JSON.parse(trimmed);
      return (
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-snug">
          {compactJson(parsed)}
        </pre>
      );
    } catch {
      // Not valid JSON, fall through
    }
  }

  // Plain value: wrap normally
  return <span className="break-words">{str}</span>;
}

function parseAuditResult(record: OPSyncAuditRunItem): OPSyncAuditResult | null {
  if (!record.AuditResultJson) return null;
  try {
    return JSON.parse(record.AuditResultJson) as OPSyncAuditResult;
  } catch {
    return {
      entityKey: record.EntityKey,
      ean: record.EAN || '',
      opProductId: record.OPProductId,
      notFoundInOP: false,
      comparisons: [],
      mismatchCount: record.MismatchCount,
      timestamp: record.UpdatedAt,
      parseError: true,
    };
  }
}

/* ---------- Field Mismatch Summary ---------- */

export function FieldMismatchTable({ data }: { data: OPSyncAuditFieldSummary[] }) {
  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Field</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead className="text-right">Count</TableHead>
            <TableHead>Sample Key</TableHead>
            <TableHead>Expected</TableHead>
            <TableHead>Actual</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={`${row.fieldName}-${i}`}>
              <TableCell className="font-mono text-xs">{row.fieldName}</TableCell>
              <TableCell>
                <Badge variant={severityVariant(row.severity)}>{row.severity}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono">{row.mismatchCount}</TableCell>
              <TableCell className="font-mono text-xs">{row.sampleEntityKey}</TableCell>
              <TableCell className="text-xs">
                <TruncatedCell value={row.sampleExpected} />
              </TableCell>
              <TableCell className="text-xs">
                <TruncatedCell value={row.sampleActual} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}

/* ---------- Audit Mismatch Records ---------- */

export function AuditResultsTable({
  records,
  total,
  isLoading,
}: {
  records: OPSyncAuditRunItem[];
  total: number;
  isLoading: boolean;
}) {
  if (isLoading) return <TableSkeleton rows={3} cols={5} />;

  return (
    <>
      {total > 0 && (
        <p className="mb-2 text-xs text-muted-foreground">
          {total} record{total !== 1 ? 's' : ''} with mismatches
        </p>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Entity Key</TableHead>
            <TableHead>EAN</TableHead>
            <TableHead className="text-right">Mismatches</TableHead>
            <TableHead>OP ID</TableHead>
            <TableHead>Last Audit</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.length === 0 ? (
            <EmptyRow colSpan={5} message="No mismatches found" />
          ) : (
            records.map((r) => (
              <TableRow key={r.EntityKey}>
                <TableCell className="font-mono text-xs">{r.EntityKey}</TableCell>
                <TableCell className="font-mono text-xs">{r.EAN || '—'}</TableCell>
                <TableCell className="text-right font-mono">
                  <span className={r.MismatchCount > 0 ? 'font-medium text-destructive' : ''}>
                    {r.MismatchCount}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.OPProductId ?? '—'}</TableCell>
                <TableCell className="text-xs">{formatDateTime(r.UpdatedAt)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </>
  );
}

export function AuditRecordReviewWorkspace({
  records,
  total,
  isLoading,
  selectedRecordKey,
  onSelectRecord,
}: {
  records: OPSyncAuditRunItem[];
  total: number;
  isLoading: boolean;
  selectedRecordKey: string | null;
  onSelectRecord: (entityKey: string) => void;
}) {
  const [showMatched, setShowMatched] = useState(false);

  if (isLoading) return <TableSkeleton rows={4} cols={6} />;

  if (records.length === 0) {
    return (
      <div className="rounded-md border">
        <div className="p-8 text-center text-sm text-muted-foreground">
          No mismatches found
        </div>
      </div>
    );
  }

  const selectedRecord =
    records.find((r) => r.EntityKey === selectedRecordKey) ?? records[0];
  const selectedIndex = records.findIndex(
    (r) => r.EntityKey === selectedRecord.EntityKey,
  );
  const parsedAuditResult = parseAuditResult(selectedRecord);
  const allComparisons = [...(parsedAuditResult?.comparisons || [])].sort(
    (a, b) => {
      if (a.match !== b.match) return a.match ? 1 : -1;
      const severityOrder = { CRITICAL: 0, WARNING: 1, INFO: 2 };
      return severityOrder[a.severity] - severityOrder[b.severity];
    },
  );
  const mismatched = allComparisons.filter((c) => !c.match);
  const matched = allComparisons.filter((c) => c.match);
  const comparisons = showMatched ? allComparisons : mismatched;
  const previousRecord =
    selectedIndex > 0 ? records[selectedIndex - 1] : null;
  const nextRecord =
    selectedIndex < records.length - 1 ? records[selectedIndex + 1] : null;

  return (
    <TooltipProvider>
      <div className="overflow-hidden rounded-md border">
        {/* Record navigator — full width, compact */}
        <div className="border-b bg-muted/30 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={!previousRecord}
                  onClick={() =>
                    previousRecord &&
                    onSelectRecord(previousRecord.EntityKey)
                  }
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                  {selectedIndex + 1} / {records.length}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={!nextRecord}
                  onClick={() =>
                    nextRecord && onSelectRecord(nextRecord.EntityKey)
                  }
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="h-4 w-px bg-border" />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0 text-sm">
                <span className="font-mono text-xs">
                  {selectedRecord.EntityKey}
                </span>
                <span className="text-xs text-muted-foreground">
                  EAN:{' '}
                  <span className="font-mono">
                    {selectedRecord.EAN || '—'}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  OP:{' '}
                  <span className="font-mono">
                    {selectedRecord.OPProductId ?? '—'}
                  </span>
                </span>
              </div>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <Badge variant="destructive">
                {mismatched.length} mismatch
                {mismatched.length === 1 ? '' : 'es'}
              </Badge>
              {matched.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setShowMatched(!showMatched)}
                >
                  {showMatched ? 'Hide' : 'Show'} {matched.length} matched
                </Button>
              )}
              {parsedAuditResult?.notFoundInOP && (
                <Badge variant="warning">Missing in OP</Badge>
              )}
            </div>
          </div>
        </div>

        {/* Full-width comparison table */}
        <div className="max-h-[70vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Field</TableHead>
                <TableHead className="w-[90px]">Result</TableHead>
                <TableHead className="w-[80px]">Severity</TableHead>
                <TableHead className="w-[35%]">Expected (Dataverse)</TableHead>
                <TableHead className="w-[35%]">Actual (OP)</TableHead>
                <TableHead className="w-[60px]">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisons.length === 0 ? (
                <EmptyRow
                  colSpan={6}
                  message="No field comparisons available"
                />
              ) : (
                comparisons.map((comparison) => (
                  <TableRow
                    key={comparison.field}
                    className={
                      comparison.match
                        ? 'bg-emerald-50/40 dark:bg-emerald-950/10'
                        : 'bg-destructive/5'
                    }
                  >
                    <TableCell className="align-top font-mono text-xs">
                      {comparison.field}
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant={comparisonResultVariant(comparison)}>
                        {comparison.match ? 'Match' : 'Mismatch'}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant={severityVariant(comparison.severity)}>
                        {comparison.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top text-xs">
                      <ComparisonValueCell value={comparison.expected} />
                    </TableCell>
                    <TableCell className="align-top text-xs">
                      <ComparisonValueCell value={comparison.actual} />
                    </TableCell>
                    <TableCell className="align-top text-xs text-muted-foreground">
                      <TruncatedCell value={comparison.notes || null} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {parsedAuditResult?.parseError && (
          <div className="border-t px-4 py-2 text-xs text-amber-600">
            Could not fully parse audit payload — results may be incomplete
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}

/* ---------- Audit Failed / Skipped Records ---------- */

export function AuditRunItemsTable({
  records,
  isLoading,
  emptyMessage,
  showError,
  showSkipReason,
}: {
  records: OPSyncAuditRunItem[];
  isLoading: boolean;
  emptyMessage: string;
  showError?: boolean;
  showSkipReason?: boolean;
}) {
  const cols = 3 + (showError ? 1 : 0) + (showSkipReason ? 1 : 0);
  if (isLoading) return <TableSkeleton rows={3} cols={cols} />;

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Entity Key</TableHead>
            <TableHead>EAN</TableHead>
            <TableHead>Status</TableHead>
            {showError && <TableHead>Error</TableHead>}
            {showSkipReason && <TableHead>Reason</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.length === 0 ? (
            <EmptyRow colSpan={cols} message={emptyMessage} />
          ) : (
            records.map((r) => (
              <TableRow key={r.EntityKey}>
                <TableCell className="font-mono text-xs">{r.EntityKey}</TableCell>
                <TableCell className="font-mono text-xs">{r.EAN || '—'}</TableCell>
                <TableCell className="text-xs font-medium">{r.AuditStatus}</TableCell>
                {showError && (
                  <TableCell>
                    <TruncatedCell value={r.LastError} className="text-xs text-destructive" />
                  </TableCell>
                )}
                {showSkipReason && (
                  <TableCell>
                    <TruncatedCell value={r.SkipReason} className="text-xs text-muted-foreground" />
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TooltipProvider>
  );
}

/* ---------- Detected Changes ---------- */

export function DetectedChangesTable({
  records,
  isLoading,
  selectedKeys,
  onSelectionChange,
}: {
  records: OPSyncAuditRunItem[];
  isLoading: boolean;
  selectedKeys: Set<string>;
  onSelectionChange: (keys: Set<string>) => void;
}) {
  if (isLoading) return <TableSkeleton rows={5} cols={5} />;

  const allSelected = records.length > 0 && records.every((record) => selectedKeys.has(record.EntityKey));

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              checked={allSelected}
              aria-label="Select all detected records"
              onCheckedChange={(checked) => {
                const next = new Set(selectedKeys);
                if (checked === true) {
                  records.forEach((record) => next.add(record.EntityKey));
                } else {
                  records.forEach((record) => next.delete(record.EntityKey));
                }
                onSelectionChange(next);
              }}
            />
          </TableHead>
          <TableHead>Entity Key</TableHead>
          <TableHead>EAN</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Modified</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.length === 0 ? (
          <EmptyRow colSpan={5} message="No detected changes" />
        ) : (
          records.map((record) => (
            <TableRow key={record.EntityKey}>
              <TableCell>
                <Checkbox
                  checked={selectedKeys.has(record.EntityKey)}
                  aria-label={`Select ${record.EntityKey}`}
                  onCheckedChange={(checked) => {
                    const next = new Set(selectedKeys);
                    if (checked === true) {
                      next.add(record.EntityKey);
                    } else {
                      next.delete(record.EntityKey);
                    }
                    onSelectionChange(next);
                  }}
                />
              </TableCell>
              <TableCell className="font-mono text-xs">{record.EntityKey}</TableCell>
              <TableCell className="font-mono text-xs">{record.EAN || '—'}</TableCell>
              <TableCell>
                <Badge variant={detectionActionVariant(record.DetectionAction)}>
                  {record.DetectionAction}
                </Badge>
              </TableCell>
              <TableCell className="text-xs">{formatDateTime(record.SourceModifiedOn)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

/* ---------- Audit Runs History ---------- */

export function AuditRunsTable({
  runs,
  isLoading,
  selectedRunId,
  onSelectRun,
}: {
  runs: OPSyncAuditRun[];
  isLoading: boolean;
  selectedRunId: string | null;
  onSelectRun: (id: string) => void;
}) {
  if (isLoading) return <TableSkeleton rows={3} cols={6} />;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Run</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Changed</TableHead>
          <TableHead className="text-right">Mismatch</TableHead>
          <TableHead className="text-right">Missing</TableHead>
          <TableHead>Started</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {runs.length === 0 ? (
          <EmptyRow colSpan={6} message="No audit runs yet" />
        ) : (
          runs.map((run) => (
            <TableRow
              key={run.AuditRunId}
              data-state={selectedRunId === run.AuditRunId ? 'selected' : undefined}
            >
              <TableCell>
                <button
                  type="button"
                  onClick={() => onSelectRun(run.AuditRunId)}
                  className="font-mono text-xs text-primary hover:underline"
                >
                  {run.AuditRunId.slice(0, 8)}
                </button>
              </TableCell>
              <TableCell>
                <Badge variant={statusVariant(run.Status)}>{run.Status}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono">{run.DetectionTotal}</TableCell>
              <TableCell className="text-right font-mono">
                <span className={run.MismatchCount > 0 ? 'text-destructive' : ''}>
                  {run.MismatchCount}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono">
                <span className={run.MissingCount > 0 ? 'text-amber-600' : ''}>
                  {run.MissingCount}
                </span>
              </TableCell>
              <TableCell className="text-xs">{formatDateTime(run.StartedAt)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

/* ---------- Full Run History ---------- */

export function FullRunHistoryTable({
  history,
  isLoading,
}: {
  history: OPSyncJobStatus[];
  isLoading: boolean;
}) {
  if (isLoading) return <TableSkeleton rows={3} cols={7} />;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Records</TableHead>
          <TableHead className="text-right">Success</TableHead>
          <TableHead className="text-right">Failed</TableHead>
          <TableHead className="text-right">Skipped</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Completed</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {history.length === 0 ? (
          <EmptyRow colSpan={7} message="No full runs yet" />
        ) : (
          history.map((job) => (
            <TableRow key={job.jobId}>
              <TableCell>
                <Badge variant={statusVariant(job.status)}>{job.status}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono">{job.totalRecords}</TableCell>
              <TableCell className="text-right font-mono">
                <span className={job.successfulRecords > 0 ? 'text-emerald-600' : ''}>
                  {job.successfulRecords}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono">
                <span className={job.failedRecords > 0 ? 'text-destructive' : ''}>
                  {job.failedRecords}
                </span>
              </TableCell>
              <TableCell className="text-right font-mono text-muted-foreground">
                {job.skippedRecords}
              </TableCell>
              <TableCell className="text-xs">{formatDateTime(job.startedAt)}</TableCell>
              <TableCell className="text-xs">{formatDateTime(job.completedAt)}</TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
